import { useEffect, useState } from "react";
import { NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
// Hapus "Heart" dari import karena sudah tidak dipakai
import { LayoutDashboard, CheckSquare, WalletCards, Wallet, Users, Briefcase, CalendarClock, FileText, Sparkles, Settings, LogOut, Menu, ChevronsLeft, ChevronsRight, CalendarDays, UsersRound, Activity, MessageCircle, Quote, MailCheck, Music, Package, Wand2, Palette, Lock, Plane, Star, BarChart2, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, API_BASE } from "@/lib/api";
import { useT } from "@/context/LanguageContext";
import LanguageToggle from "@/components/app/LanguageToggle";
import GoldPriceWidget from "@/components/app/GoldPriceWidget";
import NotificationCenter from "@/components/app/NotificationCenter";
import MaintenanceBanner from "@/components/app/MaintenanceBanner";
import { AI_PREWEDDING_ENABLED } from "@/lib/features";

const SIDEBAR_COLLAPSED_KEY = "weddly.sidebar.collapsed";

export default function AppShell({ children }) {
  const { user, wedding, logout, isAdmin } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  // Sidebar default TERBUKA (label terlihat) agar user langsung paham isinya;
  // bisa diminimize ke ikon saja dan pilihannya diingat antar kunjungan.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  // AI Prewedding masih tahap pengembangan: hanya tampil untuk admin (agar bisa
  // diuji) atau saat flag dirilis. Pengguna biasa tidak melihat menunya.
  const showPrewedding = AI_PREWEDDING_ENABLED || isAdmin;
  const NAV = [
    { name: t("nav.dashboard"), path: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { name: "Undangan Digital", path: "/invitations", icon: MailCheck, testid: "nav-invitations" },
    ...(showPrewedding
      ? [{ name: "AI Prewedding", path: "/ai-prewedding", icon: Wand2, testid: "nav-ai-prewedding" }]
      : []),
    { name: t("nav.handover"), path: "/handover", icon: Package, testid: "nav-handover" },
    { name: t("nav.checklist"), path: "/checklist", icon: CheckSquare, testid: "nav-checklist" },
    { name: t("nav.budget"), path: "/budget", icon: WalletCards, testid: "nav-budget" },
    { name: t("nav.savings"), path: "/savings", icon: Wallet, testid: "nav-savings" },
    { name: t("nav.guests"), path: "/guests", icon: Users, testid: "nav-guests" },
    { name: "Wedding Moodboard", path: "/moodboard", icon: Palette, testid: "nav-moodboard" },
    { name: t("nav.vendors"), path: "/vendors", icon: Briefcase, testid: "nav-vendors" },
    { name: t("nav.timeline"), path: "/timeline", icon: CalendarClock, testid: "nav-timeline" },
    { name: t("nav.documents"), path: "/documents", icon: FileText, testid: "nav-documents" },
    { name: "Couple Talk", path: "/couple-talk", icon: HeartHandshake, testid: "nav-couple-talk" },
    { name: "Honeymoon", path: "/honeymoon", icon: Plane, testid: "nav-honeymoon" },
    { name: t("nav.ai"), path: "/ai", icon: Sparkles, testid: "nav-ai-advisor" },
    { name: t("nav.settings"), path: "/settings", icon: Settings, testid: "nav-settings" },
  ];
  // Pengelompokan menu supaya lebih mudah dipindai daripada daftar datar
  const NAV_GROUPS = [
    { key: "main", label: t("nav.group.main"), items: NAV.filter((n) => ["/dashboard", "/invitations", "/ai-prewedding", "/handover", "/checklist", "/budget", "/savings", "/guests"].includes(n.path)) },
    { key: "planning", label: t("nav.group.planning"), items: NAV.filter((n) => ["/couple-talk", "/moodboard", "/vendors", "/timeline", "/documents", "/honeymoon", "/ai", "/settings"].includes(n.path)) },
  ];

  // Sub-menu Admin, dirender sebagai grup sendiri dan juga di sheet mobile.
  const ADMIN_NAV = [
    { name: "Pendaftaran", path: "/admin/pending", icon: UsersRound, testid: "nav-admin-pending" },
    { name: "Analitik User", path: "/admin/analytics", icon: BarChart2, testid: "nav-admin-analytics" },
    { name: "Aktivitas Akun", path: "/admin/activity", icon: Activity, testid: "nav-admin-activity" },
    { name: "WhatsApp Service", path: "/admin/whatsapp", icon: MessageCircle, testid: "nav-admin-whatsapp" },
    { name: "Masa Aktif", path: "/admin/accounts", icon: CalendarClock, testid: "nav-admin-accounts" },
    { name: "Kutipan", path: "/admin/quotes", icon: Quote, testid: "nav-admin-quotes" },
    { name: "Musik", path: "/admin/music", icon: Music, testid: "nav-admin-music" },
    { name: "Feedback", path: "/admin/feedback", icon: Star, testid: "nav-admin-feedback" },
    { name: "Log System", path: "/admin/logs", icon: FileText, testid: "nav-admin-logs" },
    { name: "Template AI", path: "/admin/prewedding-templates", icon: Wand2, testid: "nav-admin-prewedding-templates" },
  ];

  // Sheet "Lainnya" harus tertutup otomatis kapan pun pindah halaman
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // Akun pasangan yang masih memakai password sementara wajib membuat password sendiri dulu
  if (user?.must_change_password) return <Navigate to="/first-login" replace />;

  const MOBILE_MAIN_PATHS = ["/dashboard", "/invitations", "/checklist", "/budget"];
  const MORE_PATHS = ["/savings", "/guests", "/handover", "/couple-talk", "/moodboard", "/vendors", "/timeline", "/documents", "/honeymoon", "/ai", "/settings"];
  const moreItems = NAV.filter((n) => MORE_PATHS.includes(n.path));

  // Page title mapping for header
  const PAGE_TITLES = {
    "/dashboard": t("nav.dashboard"),
    "/invitations": "Undangan Digital",
    "/moodboard": "Wedding Moodboard",
    "/handover": t("nav.handover"),
    "/handover/handover": t("nav.handover"),
    "/handover/mahar": t("nav.handover"),
    "/checklist": t("nav.checklist"),
    "/budget": t("nav.budget"),
    "/savings": t("nav.savings"),
    "/guests": t("nav.guests"),
    "/vendors": t("nav.vendors"),
    "/timeline": t("nav.timeline"),
    "/documents": t("nav.documents"),
    "/couple-talk": "Couple Talk",
    "/honeymoon": "Honeymoon Planner",
    "/honeymoon/budget": "Honeymoon · Budget",
    "/honeymoon/itinerary": "Honeymoon · Itinerary",
    "/honeymoon/booking": "Honeymoon · Booking",
    "/honeymoon/checklist": "Honeymoon · Checklist",
    "/ai": t("nav.ai"),
    "/settings": t("nav.settings"),
    "/admin/pending": "Admin · Pendaftaran",
    "/admin/analytics": "Admin · Analitik User",
    "/admin/activity": "Admin · Aktivitas",
    "/admin/whatsapp": "Admin · WhatsApp",
    "/admin/accounts": "Admin · Masa Aktif",
    "/admin/quotes": "Admin · Kutipan",
    "/admin/music": "Admin · Musik",
    "/admin/feedback": "Admin · Feedback & Rating",
    "/admin/logs": "Admin · Log System",
    "/admin/prewedding-templates": "Admin · Template AI Prewedding",
    "/ai-prewedding": "AI Prewedding Studio",
  };
  const pageTitle = PAGE_TITLES[location.pathname] || t("nav.dashboard");

  return (
    // Tambahkan h-screen dan overflow-hidden agar tinggi container pas dengan layar
    <div className="h-screen flex bg-background overflow-hidden">
      
      {/* Sidebar — glassmorphism, bisa dibuka (label) / diminimize (ikon saja) */}
      <aside
        data-testid="app-sidebar"
        className={`hidden md:flex md:flex-col shrink-0 border-r border-border/60 glass-sidebar sticky top-0 h-screen z-40 transition-[width] duration-300 ease-in-out ${collapsed ? "w-20" : "w-64"}`}
      >
        {/* Logo + Judul Wedding Planner + Sub-judul BY WEDDLY INDONESIA */}
        <div className={`py-3.5 border-b border-border/50 shrink-0 flex items-center justify-between transition-all duration-300 ${collapsed ? "px-3" : "px-4"}`}>
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/weddly-logo.png"
              alt="Logo Weddly"
              title="Weddly"
              className={`object-contain shrink-0 transition-all duration-300 ${collapsed ? "h-10 w-10" : "h-12 md:h-14 w-auto max-h-14"}`}
            />
            {!collapsed && (
              <div className="flex flex-col min-w-0 leading-tight">
                <span className="font-serif text-[15px] font-semibold tracking-tight text-foreground truncate">
                  Wedding Planner
                </span>
                <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground/75 truncate mt-0.5">
                  BY WEDDLY
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            title={collapsed ? t("nav.expand") : t("nav.collapse")}
            className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent/70 transition-colors duration-150"
          >
            {collapsed ? (
              <ChevronsRight strokeWidth={1.5} className="h-[18px] w-[18px]" />
            ) : (
              <ChevronsLeft strokeWidth={1.5} className="h-[18px] w-[18px]" />
            )}
          </button>
        </div>

        {/* Menu dengan ikon + label, dikelompokkan agar mudah dipindai */}
        <nav className="px-2.5 pt-4 flex-1 flex flex-col gap-5 overflow-y-auto overflow-x-hidden">
          {NAV_GROUPS.map((group) => (
            <div key={group.key}>
              {!collapsed && (
                <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
                  {group.label}
                </div>
              )}
              <div className="space-y-1">
                {group.items.map((n) => {
                  const isRestricted = !isAdmin && user?.plan_type === "trial" && ["/invitations", "/moodboard", "/honeymoon", "/ai", "/ai-prewedding"].includes(n.path);
                  return (
                    <NavLink
                      key={n.path}
                      to={n.path}
                      data-testid={n.testid}
                      title={n.name}
                      className={({ isActive }) =>
                        `flex items-center rounded-xl text-sm font-medium transition-all duration-150 ${
                          collapsed ? "justify-center p-3" : "gap-3 px-3 py-2.5"
                        } ${isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-accent/70"}`
                      }
                    >
                      <n.icon strokeWidth={1.5} className="h-[18px] w-[18px] shrink-0" />
                      {!collapsed && <span className="truncate whitespace-nowrap">{n.name}</span>}
                      {isRestricted && (
                        collapsed ? (
                          <Lock className="h-3 w-3 text-amber-600 dark:text-amber-400 absolute top-1 right-1" />
                        ) : (
                          <span className="ml-auto text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 px-1.5 py-0.5 rounded border border-amber-300 dark:border-amber-700">PRO</span>
                        )
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
          {isAdmin && (
            <>
              {!collapsed && (
                <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
                  {t("nav.admin")}
                </div>
              )}
              <div className="space-y-1">
                {ADMIN_NAV.map((n) => (
                  <NavLink
                    key={n.path}
                    to={n.path}
                    data-testid={n.testid}
                    title={n.name}
                    className={({ isActive }) =>
                      `flex items-center rounded-xl text-sm font-medium transition-all duration-150 ${
                        collapsed ? "justify-center p-3" : "gap-3 px-3 py-2.5"
                      } ${isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-accent/70"}`
                    }
                  >
                    <n.icon strokeWidth={1.5} className="h-[18px] w-[18px] shrink-0" />
                    {!collapsed && <span className="truncate whitespace-nowrap">{n.name}</span>}
                  </NavLink>
                ))}
              </div>
            </>
          )}
        </nav>

        {/* Footer: profil user, bahasa, keluar */}
        <div className={`border-t border-border/60 py-4 shrink-0 ${collapsed ? "px-2 flex flex-col items-center gap-3" : "px-4 flex flex-col gap-3"}`}>
          <div className={`flex items-center w-full min-w-0 ${collapsed ? "flex-col justify-center gap-2" : "gap-2.5"}`}>
            {user?.has_avatar ? (
              <img src={`${API_BASE}/users/${user.user_id}/avatar`} alt="" title={user?.name || user?.email} className="h-9 w-9 rounded-full object-cover ring-1 ring-border shrink-0" />
            ) : user?.picture ? (
              <img src={user.picture} alt="" title={user?.name || user?.email} className="h-9 w-9 rounded-full ring-1 ring-border shrink-0" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-muted ring-1 ring-border shrink-0" />
            )}
            {!collapsed && (
              <div className="flex-1 min-w-0 leading-tight">
                <div className="text-sm font-medium truncate" title={user?.name || user?.email}>{user?.name || user?.email}</div>
                {user?.name && user?.email && (
                  <div className="text-[11px] text-muted-foreground truncate">{user.email}</div>
                )}
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" title={t("nav.logout")} className="rounded-full text-muted-foreground hover:text-foreground shrink-0">
              <LogOut strokeWidth={1.5} className="h-[18px] w-[18px]" />
            </Button>
          </div>
          {collapsed ? <LanguageToggle compact /> : <LanguageToggle className="self-start" />}
        </div>
      </aside>

      {/* Bagian konten utama bisa di-scroll */}
      <main className="flex-1 min-w-0 overflow-y-auto pb-20 md:pb-0">
        <MaintenanceBanner />
        {/* Header bar — sticky top: judul halaman kiri (hanya di beranda), widget emas & notifikasi kanan */}
        <header className="sticky top-0 z-30 crystal-header px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-4">
          {(location.pathname === "/dashboard" || location.pathname === "/") ? (
            <h1 className="font-serif text-xl md:text-2xl font-semibold truncate">{pageTitle}</h1>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {!isAdmin && user && (
              <div
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                style={{
                  backgroundColor: user.plan_type === "trial" ? "rgba(59, 130, 246, 0.08)" : "rgba(16, 185, 129, 0.08)",
                  borderColor: user.plan_type === "trial" ? "rgba(59, 130, 246, 0.3)" : "rgba(16, 185, 129, 0.3)",
                  color: user.plan_type === "trial" ? "#1d4ed8" : "#047857"
                }}
                data-testid="user-plan-badge"
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: user.plan_type === "trial" ? "#2563eb" : "#10b981" }}
                />
                {user.plan_type === "trial"
                  ? `Trial (${user.plan_expires_at ? Math.max(0, Math.ceil((new Date(user.plan_expires_at) - new Date()) / (1000 * 60 * 60 * 24))) : 14}H)`
                  : "Premium"}
              </div>
            )}
            {/* Widget Harga Emas Hari Ini */}
            <GoldPriceWidget />
            {/* Pusat Notifikasi & Pengingat */}
            <NotificationCenter />
          </div>
        </header>
        {children}
      </main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-xl border-t border-border h-16 flex items-center justify-between px-1">
        {NAV.filter((n) => MOBILE_MAIN_PATHS.includes(n.path)).map((n) => (
          <NavLink
            key={n.path}
            to={n.path}
            data-testid={`mobile-${n.testid}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] py-1 rounded-lg transition-colors ${
                isActive ? "text-primary font-semibold" : "text-muted-foreground"
              }`
            }
          >
            <n.icon className="h-5 w-5 shrink-0" />
            <span className="truncate max-w-[64px] text-center">
              {n.path === "/invitations" ? "Undangan" : n.name}
            </span>
          </NavLink>
        ))}
        <button
          type="button"
          data-testid="mobile-nav-more"
          onClick={() => setMoreOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] py-1 rounded-lg text-muted-foreground"
        >
          <Menu className="h-5 w-5 shrink-0" />
          <span className="truncate max-w-[64px] text-center">{t("nav.more")}</span>
        </button>
      </nav>

      {/* Sheet "Lainnya" — hanya dijangkau dari bottom nav mobile */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] flex flex-col rounded-t-3xl px-5 pt-6 pb-8 overflow-hidden" data-testid="mobile-more-sheet">
          <SheetHeader className="mb-4 shrink-0 text-left">
            <SheetTitle>{t("nav.more")}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto pr-1 pb-4 grid grid-cols-2 gap-3" data-testid="mobile-more-grid">
            {moreItems.map((n) => {
              const active = location.pathname === n.path;
              return (
                <button
                  key={n.path}
                  type="button"
                  data-testid={`mobile-more-${n.testid}`}
                  onClick={() => { setMoreOpen(false); navigate(n.path); }}
                  className={`flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-colors duration-150 ${active ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-accent"}`}
                >
                  <n.icon className="h-5 w-5" />
                  <span className="text-sm font-medium">{n.name}</span>
                </button>
              );
            })}
            {isAdmin && ADMIN_NAV.map((n) => {
              const active = location.pathname.startsWith(n.path);
              return (
                <button
                  key={n.path}
                  type="button"
                  data-testid={`mobile-more-${n.testid}`}
                  onClick={() => { setMoreOpen(false); navigate(n.path); }}
                  className={`flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-colors duration-150 ${active ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-accent"}`}
                >
                  <n.icon className="h-5 w-5" />
                  <span className="text-sm font-medium">{n.name}</span>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}